import { DraggableItemList, DuplicateBanner } from '@components'

import { Divider, Flex, FormItemsGroup, Module, ModuleGroup, Space, Timeout } from '@billy/design-system'
import { ReactElement, useCallback, useEffect, useRef } from 'react'

import { EInvoiceStatus } from '@features/eInvoicing/constants/eInvoiceStatus'
import { EInvoicingCashedBanner } from '@features/eInvoicing/elements/EInvoicingCashedBanner'
import { useUserOrganization, useUserOrganizationSettings } from '@modules-deprecated/app/organization'
import { UiSetup } from '@modules-deprecated/app/organization/enums/uiSetup'
import { ExpensesTrackingEvents } from '@views/receipts/enums/tracking'

import { useFieldArray, useFormContext, useSegment } from '../../../../hooks'
import { BILL_FORM_BASE_MODULE_ID } from '../../constants/billFormBaseModuleId'
import { BillEditableActions } from '../BillEditableActions'
import { useBillForm } from './contexts/billFormContext'
import { BillAddLineButton } from './elements/BillAddLineButton'
import { BillBillDate } from './elements/BillBillDate'
import { BillBookkeepingTagSuggestion } from './elements/BillBookkeepingTagSuggestion'
import { BillCurrencySelect } from './elements/BillCurrencySelect'
import { BillDueDate } from './elements/BillDueDate'
import { BillEInvoiceCompletedBanner } from './elements/BillEInvoiceCompletedBanner'
import { BillEInvoiceLifecycleBanner } from './elements/BillEInvoiceLifecycleBanner'
import { BillLine } from './elements/BillLine'
import { BillNumber } from './elements/BillNumber'
import { BillReferenceNumberInput } from './elements/BillReferenceNumberInput'
import { BillRefusedInfo } from './elements/BillRefusedInfo'
import { BillRegisterPayment } from './elements/BillRegisterPayment'
import { BillStatusBadge } from './elements/BillStatusBadge'
import { BillSummary } from './elements/BillSummary'
import { BillVendor } from './elements/BillVendor'
import * as Styled from './styles'
import { BillFormLine, BillFormSchema } from './utils/formData'

const AUTO_FOCUS_TIMEOUT = 1000

export const BillEditableForm = (): ReactElement => {
  const { organization } = useUserOrganization()
  const { organizationSettings } = useUserOrganizationSettings()
  const {
    isCreditNote,
    isNewCreditNote,
    isCreditNoteFromBill,
    billId,
    attachmentId,
    eInvoiceStatus,
    isLifecycleLocked,
    eInvoiceId,
    eInvoiceReasonCode,
    overrideLifecycleState,
    isDuplicate,
    onOpenDuplicate,
    modalDisplayMode,
  } = useBillForm()

  const isCompleted = eInvoiceStatus === EInvoiceStatus.Completed
  const isDisputed = eInvoiceStatus === EInvoiceStatus.Disputed
  const isRefused = eInvoiceStatus === EInvoiceStatus.Refused
  const billFormWrapperRef = useRef<HTMLDivElement>(null)
  const focusTimeoutRef = useRef<Timeout>()
  const { track } = useSegment()

  // This is a hack to present the "Register Payment"
  // button to DK users only. We're not using scopes.
  // FR/DE/NL orgs with this ui setup are < 1000. DK is 230K at the time of writing
  const isAccountingUi = organization?.uiSetup === UiSetup.Accounting
  const showRegisterPaymentButton =
    isAccountingUi &&
    !isCreditNote &&
    !isNewCreditNote &&
    !isCreditNoteFromBill &&
    !isLifecycleLocked &&
    !modalDisplayMode

  const withReferenceNumberField = organizationSettings?.supplierInvoiceNoEnabled === '1'

  const { control } = useFormContext<BillFormSchema>()
  const {
    fields: billLines,
    append: addLine,
    remove: removeLine,
    move: moveLine,
  } = useFieldArray({
    control,
    name: 'billLines',
  })

  const linesTotal = billLines.length

  const handleAddLine = useCallback(
    (billLine: BillFormLine) => {
      addLine(billLine)
    },
    [addLine],
  )

  const handleDeleteLineClick = useCallback(
    (index: number) => () => {
      removeLine(index)
    },
    [removeLine],
  )

  const handleLineOrderChange = useCallback(
    (oldIndex: number, newIndex: number) => {
      moveLine(oldIndex, newIndex)
    },
    [moveLine],
  )

  const focusOnInput = useCallback((inputOrder = 0) => {
    if (!billFormWrapperRef.current) {
      return
    }

    if (!billFormWrapperRef.current.contains(document?.activeElement)) {
      const inputs = billFormWrapperRef.current.getElementsByTagName('input')
      inputs[inputOrder]?.focus()
    }
  }, [])

  const handleNewContactCreated = useCallback(() => {
    track(ExpensesTrackingEvents.SupplierCreated, {
      source: 'Purchases',
    })
    focusOnInput(1)
  }, [focusOnInput, track])

  const handleDuplicateReviewClick = useCallback(() => {
    track(ExpensesTrackingEvents.DuplicateReviewClicked)
    onOpenDuplicate?.()
  }, [onOpenDuplicate, track])

  useEffect(() => {
    focusTimeoutRef.current = setTimeout(() => {
      focusOnInput()
    }, AUTO_FOCUS_TIMEOUT) // eslint-disable-next-line react-hooks/exhaustive-deps -- Legacy from 2022
  }, [billId, attachmentId])

  useEffect(() => {
    return () => {
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current)
      }
    }
  }, [])

  return (
    <Styled.BillFormWrapper ref={billFormWrapperRef}>
      <ModuleGroup>
        <Module id={BILL_FORM_BASE_MODULE_ID}>
          {isRefused && <BillRefusedInfo />}
          {isDuplicate && onOpenDuplicate && <DuplicateBanner onPress={handleDuplicateReviewClick} />}
          {isLifecycleLocked && (
            <BillEInvoiceLifecycleBanner
              status={isDisputed ? EInvoiceStatus.Disputed : EInvoiceStatus.Suspended}
              eInvoiceId={eInvoiceId}
              reasonCode={eInvoiceReasonCode}
              onChangeStatus={overrideLifecycleState}
            />
          )}
          {isCompleted && <BillEInvoiceCompletedBanner />}
          <EInvoicingCashedBanner
            status={eInvoiceStatus}
            messageKey="receipts.e_invoice.status.payment_marked_paid_banner_message_short"
          />
          <Styled.FormFieldset disabled={isRefused || isLifecycleLocked}>
            <Styled.FormHeader>
              <BillNumber />
              <BillStatusBadge />
            </Styled.FormHeader>
            <Space size="s8" />

            <FormItemsGroup itemsInRow={withReferenceNumberField ? 4 : 1}>
              <BillVendor onCreate={handleNewContactCreated} withReferenceNumberField={withReferenceNumberField} />
              {withReferenceNumberField && <BillReferenceNumberInput />}
            </FormItemsGroup>

            <FormItemsGroup itemsInRow={4}>
              <BillBillDate />
              <Styled.ItemWrapper>
                <BillDueDate />
              </Styled.ItemWrapper>
              <Styled.ItemWrapper />
              <Styled.ItemWrapper>
                <BillCurrencySelect />
              </Styled.ItemWrapper>
            </FormItemsGroup>

            <BillBookkeepingTagSuggestion />
            <Divider />

            {/* Lines: */}
            <DraggableItemList id="bill-lines" isDraggable={linesTotal > 1} onOrderChange={handleLineOrderChange}>
              {billLines.map((billLine, index) => (
                <BillLine
                  id={billLine.id}
                  sessionId={billLine.sessionId}
                  index={index}
                  key={billLine.id}
                  linesTotal={linesTotal}
                  onLineDelete={handleDeleteLineClick(index)}
                />
              ))}
            </DraggableItemList>

            <Space size="s8" />
            <BillAddLineButton onAddLine={handleAddLine} linesTotal={linesTotal} />
            <Space size="s8" />

            {showRegisterPaymentButton && <BillRegisterPayment />}
          </Styled.FormFieldset>
        </Module>

        <BillSummary />

        {modalDisplayMode && (
          <Flex justifyContent="flex-end">
            <BillEditableActions />
          </Flex>
        )}
      </ModuleGroup>
    </Styled.BillFormWrapper>
  )
}
